/**
 * Set Status Business Logic
 * Shared utilities for determining set status and button content
 */

import type { ProcessedSet, Review } from "@workspace/types";
import { SET_STATUSES, type SetStatus } from "@workspace/types";

// ===== STATUS TYPES =====
export type { SetStatus } from "@workspace/types";

export interface SetStatusInfo {
	status: SetStatus;
	hasStatus: boolean;
}

// ===== STATUS DETERMINATION =====

/**
 * Determines the primary status of a set based on review and schedule state
 * Follows precedence: sawLive > sawStream > mustSee > scheduled > default
 */
export function getSetStatus(review: Review | undefined): SetStatusInfo {
	if (review?.sawLive)
		return { status: SET_STATUSES.SAW_LIVE, hasStatus: true };
	if (review?.sawStream)
		return { status: SET_STATUSES.SAW_STREAM, hasStatus: true };
	if (review?.mustSee)
		return { status: SET_STATUSES.MUST_SEE, hasStatus: true };
	if (review?.wantToSee)
		return { status: SET_STATUSES.SCHEDULED, hasStatus: true };
	return { status: SET_STATUSES.DEFAULT, hasStatus: false };
}

// ===== BUTTON CONTENT LOGIC =====

/**
 * Gets button title text based on status and set timing
 * Note: For determining if set is past, platforms should use their own isSetPast implementation
 */
export function getSetButtonTitle(
	status: SetStatus,
	isPast?: boolean,
): string {
	switch (status) {
		case SET_STATUSES.SAW_LIVE:
			return "Saw Live";
		case SET_STATUSES.SAW_STREAM:
			return "Saw Stream";
		case SET_STATUSES.MUST_SEE:
			return "Must See";
		case SET_STATUSES.SCHEDULED:
			// Use isPast parameter if provided, otherwise default to present tense
			return isPast ? "Wanted to See" : "Want to See";
		default:
			return "Add to List";
	}
}

/**
 * Gets button content text only (no icons) based on status and set timing
 * Alias for getSetButtonTitle for backward compatibility
 */
export function getSetButtonText(
	status: SetStatus,
	isPast?: boolean,
): string {
	return getSetButtonTitle(status, isPast);
}